/**
 * #2120 — the `route` a "Report a translation" report carries, with every
 * secret taken out of the path. A query and a hash are dropped (magic-link and
 * reset tokens live there), and so are tokens that live IN the path:
 *
 *   - `/provider-upload/<token>` → `/provider-upload/:token`. That page is
 *     opened from a link whose path segment is a bearer token.
 *   - any other long opaque segment (32+ characters with no word-slug shape,
 *     e.g. a hex token or a UUID) → `:token`. A 24-character ObjectId stays,
 *     so a report still says which record's page it came from.
 *
 * The server applies the same rule (backend/src/routes/translation-reports.js
 * `maskRouteTokens`), so an old client cannot store a token either. Keep the
 * two in step.
 */

const TOKEN_PLACEHOLDER = ':token';

/** Routes whose segment after the prefix is a bearer token, whatever its length. */
const TOKEN_ROUTE_PREFIXES = ['provider-upload'];

const OPAQUE_MIN_LENGTH = 32;
const OPAQUE_SEGMENT = /^[A-Za-z0-9._~%+=-]+$/;
/** Lowercase words joined by hyphens, e.g. `monthly-check-in` — a slug, not a secret. */
const WORD_SLUG = /^[a-z]+(?:-[a-z]+)+$/;

function isOpaque(segment: string): boolean {
  return segment.length >= OPAQUE_MIN_LENGTH && OPAQUE_SEGMENT.test(segment) && !WORD_SLUG.test(segment);
}

export function maskRouteTokens(path: string): string {
  const bare = path.split(/[?#]/)[0];
  const segments = bare.split('/');
  return segments
    .map((segment, i) => {
      if (!segment) return segment;
      if (i > 0 && TOKEN_ROUTE_PREFIXES.includes(segments[i - 1])) return TOKEN_PLACEHOLDER;
      return isOpaque(segment) ? TOKEN_PLACEHOLDER : segment;
    })
    .join('/');
}
